/* Make the player use Rue's collected audio, and stop it guessing.
 *
 * THE BUG
 * paranoise-player.js never reads the collected previews. When a product has
 * no catalog entry it asks iTunes for the album by NAME and renders whatever
 * comes back as the tracklist. On "Various — Time Flies", a 2023 techno 12" on
 * K S R, iTunes returned Jim Lauderdale's country album of the same name, and
 * the shop played country over a techno record.
 *
 * WHAT THIS DOES
 * Two things, neither of which requires editing paranoise-player.js:
 *
 *   1. FEEDS IT THE VERIFIED AUDIO. The player already loads a per-handle
 *      override file via window.ParanoiseOverridesUrl and honours
 *      mode:"force". We hand it a blob URL holding just this product's entry,
 *      so it forces the real tracklist and the real per-track previews. Its own
 *      resolveUrl() returns early for any track that arrives with a url, so a
 *      forced track is never name-searched.
 *
 *   2. GUARDS THE FALLBACK. For records we hold nothing for, the name search
 *      still runs — that is the agreed hybrid — but its result is now checked
 *      against the record's real artist and title. Anything that does not match
 *      is removed rather than played. Rue's standing rule is that a gap beats a
 *      wrong value; silence beats a wrong preview.
 *
 * Tracks deliberately carry no url when their only stored source was an Apple
 * .p.m4a (DRM, unplayable in <audio> — the player rejects them itself). Those
 * fall back per track, and the guard in (2) still applies to them.
 */
(function () {
  'use strict';

  /* ── 1. hand the player this product's verified entry ─────────────────
     Set before paranoise-player.js runs its DOMContentLoaded init, which is
     what reads window.ParanoiseOverridesUrl. A blob URL keeps it to the one
     entry we care about instead of a multi-megabyte shared file. */
  function installEntry(entry, handle) {
    if (!entry || !entry.tracks || !entry.tracks.length) return false;
    try {
      var doc = {};
      doc[handle] = entry;
      var blob = new Blob([JSON.stringify(doc)], { type: 'application/json' });
      window.ParanoiseOverridesUrl = URL.createObjectURL(blob);
      return true;
    } catch (e) {
      return false;
    }
  }

  var handle = (function () {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  })();

  var forced = false;
  if (handle && window.PN_TRACK_PREVIEWS) {
    /* Preferred path: this product's own entry, rendered inline from its
       metafield. About 1 KB, and no extra request. */
    forced = installEntry(window.PN_TRACK_PREVIEWS, handle);
  } else if (window.PN_PREVIEWS_URL) {
    /* Interim path while the metafields are not yet written: point the player
       at a shared file of the highest-risk records. The player XHRs it itself,
       so there is no race with its DOMContentLoaded init — but every product
       page pays for the whole file, which is why this is not the end state. */
    window.ParanoiseOverridesUrl = window.PN_PREVIEWS_URL;
    forced = 'shared';
  }
  window.PN_AUDIO_FORCED = forced;

  /* ── 2. don't let a name search play the wrong record ────────────────── */

  function norm(s) {
    return String(s || '').toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  }

  /* same shape as the player's own matchScore, so behaviour stays familiar */
  function score(a, b) {
    var na = norm(a), nb = norm(b);
    if (!na || !nb) return 0;
    if (na === nb) return 1;
    if (na.indexOf(nb) !== -1 || nb.indexOf(na) !== -1) return 0.85;
    var wa = na.split(' '), wb = nb.split(' ');
    var common = wa.filter(function (w) { return w.length > 2 && wb.indexOf(w) !== -1; }).length;
    return common / Math.max(wa.length, wb.length);
  }

  var GENERIC = /^(various|various artists|v\/a|unknown|unknown artist)$/;

  /* Is this rendered row plausibly FROM the record on the page?

     A compilation credits each track to its own artist, so the record's artist
     ("Various") tells us nothing and the album name has to carry the weight.
     Where the record has a real artist, that artist must show up. */
  function plausible(rowArtist, rowRecord, trueArtist, trueTitle) {
    if (!trueArtist || GENERIC.test(norm(trueArtist))) {
      /* A compilation credited to "Various" gives a name search nothing to
         match on but the album title, and a title like "Time Flies" is not
         unique to anybody — Jim Lauderdale's country album carries that name
         too, scores a perfect 1.0 against it, and is the record that actually
         played. No threshold fixes this, because the search genuinely cannot
         tell the two apart. So a weak-name record never plays a searched
         result: it plays our verified audio or it plays nothing. */
      return false;
    }
    /* A real artist name is a strong signal, so a searched result is allowed
       through only when the artist AND the album both point at this record. */
    return score(rowArtist, trueArtist) >= 0.5 && score(rowRecord, trueTitle) >= 0.5;
  }

  /* Every URL we ourselves supplied for THIS product. A row playing one of
     these is verified by definition and is never second-guessed; anything else
     on the page arrived from a name search and has to earn its place.

     Trust is per TRACK, not per record: a covered record can still have
     individual tracks with no stored url (an Apple .p.m4a we refuse to emit),
     and those tracks fall back exactly like an uncovered record's would. */
  var ourUrls = Object.create(null);
  function rememberOurUrls(entry) {
    if (!entry || !entry.tracks) return;
    entry.tracks.forEach(function (t) { if (t.url) ourUrls[t.url] = true; });
  }
  if (window.PN_TRACK_PREVIEWS) rememberOurUrls(window.PN_TRACK_PREVIEWS);
  else if (window.PN_PREVIEWS_URL && handle) {
    /* the player is fetching this same file, so this is a cache hit */
    fetch(window.PN_PREVIEWS_URL)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (doc) { if (doc && doc[handle]) { rememberOurUrls(doc[handle]); guard(); } })
      .catch(function () {});
  }

  /* Where the audio came from, decided by HOST rather than by waiting for a
     list to load. The collected previews and the live search draw from
     completely disjoint sets of servers — the shops Rue actually buys from on
     one side, Deezer's CDN and Apple's preview host on the other — so the host
     answers "is this ours?" instantly and correctly.

     This matters because the answer is needed the moment the tracklist renders:
     an earlier version waited for the previews file and silenced four perfectly
     good verified tracks that had simply arrived first. */
  var OUR_HOSTS = /(^|\.)(vinylfuture\.com|deejay\.de|beatport\.com|forcedexposure\.com|clone\.nl|hhv\.de|decks\.de)$/i;
  function isOurs(url) {
    if (!url) return false;
    if (ourUrls[url]) return true;
    var host;
    try { host = new URL(url, window.location.href).hostname; } catch (e) { return false; }
    if (OUR_HOSTS.test(host)) return true;
    /* Forced Exposure serves its previews from a bucket path, so the host alone
       is too broad — require the folder they use */
    if (host === 's3.amazonaws.com') return url.indexOf('/fe-mp3/') !== -1;
    return false;
  }

  function guard() {
    var truth = window.PN_PRODUCT_TRUTH;
    if (!truth) return true;
    var mount = document.getElementById('pn-tracklist-mount');
    if (!mount) return true;
    var rows = mount.querySelectorAll('.pn-tracklist-item');
    if (!rows.length) return false;                   // not rendered yet

    var removed = 0;
    rows.forEach(function (row) {
      var btn = row.querySelector('.pn-track-trigger');
      if (!btn || btn.disabled) return;
      if (isOurs(btn.dataset.url)) return;                        // verified by us
      if (plausible(btn.dataset.artist, btn.dataset.record, truth.artist, truth.title)) return;
      /* Strip the audio and disable the row rather than deleting it: the track
         listing may still be right even when the matched AUDIO is not, and a
         shopper is better served by a silent row than by another record. */
      btn.dataset.url = '';
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-label', 'No verified preview available');
      row.classList.add('pn-track--unverified');
      removed++;
    });
    if (removed) {
      window.PN_AUDIO_REJECTED = removed;
      if (window.console && console.info) {
        console.info('[paranoise] ' + removed + ' preview(s) withheld: did not match ' +
          truth.artist + ' — ' + truth.title);
      }
    }
    return true;
  }

  function watch() {
    if (guard()) return;
    var mount = document.getElementById('pn-tracklist-mount');
    if (!mount) return;
    var obs = new MutationObserver(function () { guard(); });
    obs.observe(mount, { childList: true, subtree: true });
    /* the player resolves some tracklists through two or three async hops, so
       keep watching for a while rather than only for the first render */
    setTimeout(function () { obs.disconnect(); guard(); }, 20000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
